import * as THREE from 'three';
import { G, mesh } from '../render/scene';
import { MAT_P, coreGlow } from '../render/materials';
import { bevel, joint, mark, plate, ram, rib, seam } from './mech-kit';
/* ============================================================
   推進器 THRUSTER：骨盆 + 逆關節腿 + 背部推進器（1:1 移植自 mech_sample_ani.html）
   ============================================================

   剪影的主要來源。腿的質量集中在關節上方（股上段厚、腓腸鼓、踝收細）——
   這是「健體感」在肢體上的作法。 */

/* 推進器 THRUSTER：骨盆 + 逆關節腿 + 背部推進器。剪影的主要來源。
   兩腿角度不同（重心偏在左腳），各自算落差再用髖高補償，所以怎麼擺都貼地；
   兩髖的高低差換算成骨盆傾角 —— 重心偏移會讓另一邊的髖下沉，這是站姿的真相。 */
function digitigradeLeg(side, cfg) {
  var hip = new THREE.Group();
  hip.position.set(side * 0.48, 0, 0);          // 髖高由動畫層每幀寫入 lower.position.y
  hip.rotation.y = -side * 0.06;
  hip.add(joint(0.23, [0, 0, 0], true));                                       // 髖（軸端外露）

  // 慣例：+rotation.x 把向下的子節點帶往 -Z（後方），負值往前。
  var thigh = new THREE.Group(); thigh.userData.bone = 1; hip.add(thigh);
  thigh.add(bevel(MAT_P.dark,  cfg.tw, 0.56, cfg.tw * 0.94, [0, -0.28, 0]));         // 股上段（厚）
  thigh.add(bevel(MAT_P.dark,  cfg.tw * 0.72, 0.52, cfg.tw * 0.72, [0, -0.78, 0]));  // 股下段（收）
  thigh.add(plate(MAT_P.armor, cfg.tw * 1.10, 0.60, cfg.tw * 0.58, [0, -0.30, cfg.tw * 0.54], -0.12));
  thigh.add(seam (cfg.tw * 1.00, 0.05, 0.10, [0, -0.58, cfg.tw * 0.58], -0.12));
  thigh.add(rib  (MAT_P.armor, 0.07, 0.76, 0.07, [side * cfg.tw * 0.54, -0.40, cfg.tw * 0.28], -0.08));
  thigh.add(ram  ([ cfg.tw * 0.42, -0.74, cfg.tw * 0.44], 0.62, 0.10, 0.08, [-0.14, 0, 0])); // 膝液壓 ×2
  thigh.add(ram  ([-cfg.tw * 0.42, -0.74, cfg.tw * 0.44], 0.62, 0.10, 0.08, [-0.14, 0, 0]));

  var knee = new THREE.Group(); knee.position.set(0, -1.02, 0); thigh.add(knee);
  knee.add(joint(0.20, [0, 0, 0], true));

  var shin = new THREE.Group(); shin.userData.bone = 1; knee.add(shin);
  shin.add(bevel(MAT_P.dark,  cfg.sw, 0.52, cfg.sw * 1.06, [0, -0.26, 0]));          // 腓腸（高處鼓）
  shin.add(bevel(MAT_P.dark,  cfg.sw * 0.64, 0.48, cfg.sw * 0.64, [0, -0.72, 0]));   // 踝上（收細）
  shin.add(plate(MAT_P.armor, cfg.sw * 1.06, 0.56, cfg.sw * 0.54, [0, -0.28, -cfg.sw * 0.50], 0.14));
  shin.add(rib  (MAT_P.armor, 0.07, 0.62, 0.07, [0, -0.30, cfg.sw * 0.50], -0.06));
  if (cfg.hyd) {
    shin.add(ram([ cfg.sw * 0.48, -0.34, cfg.sw * 0.34], 0.68, 0.11, 0.09));
    shin.add(ram([-cfg.sw * 0.48, -0.34, cfg.sw * 0.34], 0.68, 0.11, 0.09));
  }

  var ankle = new THREE.Group(); ankle.position.set(0, -0.95, 0); shin.add(ankle);
  ankle.add(joint(0.17, [0, 0, 0]));

  var foot = new THREE.Group(); foot.userData.bone = 1; ankle.add(foot);
  foot.add(mesh (G.box,   MAT_P.trim,  [cfg.fw, 0.13, cfg.fl], [0, -0.10, cfg.fl * 0.22]));
  foot.add(plate(MAT_P.armor, cfg.fw * 0.86, 0.14, cfg.fl * 0.44, [0, -0.02, cfg.fl * 0.30], -0.10));
  foot.add(rib  (MAT_P.armor, cfg.fw * 0.78, 0.07, 0.07, [0, 0.04, cfg.fl * 0.50], -0.10));
  foot.add(bevel(MAT_P.dark,  cfg.fw * 0.66, 0.16, 0.26, [0, -0.06, -cfg.fl * 0.26]));
  return { root: hip, thigh: thigh, shin: shin, foot: foot, side: side };
}

function thrusterCfg(shape) {
  return shape === 'thr-ring'  ? { tw: 0.38, sw: 0.34, fw: 0.50, fl: 1.06, hyd: false }
       : shape === 'thr-heavy' ? { tw: 0.56, sw: 0.50, fw: 0.68, fl: 1.20, hyd: true }
       :                         { tw: 0.47, sw: 0.42, fw: 0.58, fl: 1.12, hyd: false };
}

export function buildThruster(shape, P?) {
  P = P || { glow: coreGlow('core-std') };
  var g = new THREE.Group();
  mark(g, shape, ['thr-ring', 'thr-heavy', 'thr-twin']);
  var cfg = thrusterCfg(shape);

  // 骨盆裝甲放在會傾斜的子群組裡；腿掛在外面，各自算自己的髖高
  var pel = new THREE.Group(); pel.userData.bone = 1;
  pel.position.y = -0.07;          // 骨盆下沉，維持胸底與骨盆頂之間 0.16 的穿透縫
  g.add(pel);
  pel.add(bevel(MAT_P.dark,  1.02, 0.44, 0.88, [0, 0.06, 0]));
  pel.add(plate(MAT_P.armor, 0.32, 0.50, 0.76, [ 0.60, 0.02, 0], 0, -0.18, -0.10));
  pel.add(plate(MAT_P.armor, 0.32, 0.50, 0.76, [-0.60, 0.02, 0], 0,  0.18,  0.10));
  pel.add(plate(MAT_P.armor, 0.88, 0.46, 0.22, [0, -0.18,  0.44],  0.36));
  pel.add(plate(MAT_P.armor, 0.96, 0.52, 0.22, [0, -0.14, -0.46], -0.30));
  pel.add(rib  (MAT_P.armor, 0.08, 0.08, 0.84, [ 0.63, 0.26, 0], 0, -0.18));
  pel.add(rib  (MAT_P.armor, 0.08, 0.08, 0.84, [-0.63, 0.26, 0], 0,  0.18));

  var L = digitigradeLeg( 1, cfg); g.add(L.root);                              // 左腿
  var R = digitigradeLeg(-1, cfg); g.add(R.root);                              // 右腿
  g.userData.rig = { pelvis: pel, legs: [L, R], cfg: cfg };
  return g;
}

/* 背部推進器：與下半身共用同一個 shape 代號。
   把噴口從腰後移到背上，質量集中在肩線附近 —— 60px 下讀得到，腰後讀不到。 */
export function buildBackUnit(shape, P?) {
  P = P || { glow: coreGlow('core-std') };
  var g = new THREE.Group();
  mark(g, shape, ['thr-ring', 'thr-heavy', 'thr-twin']);
  var cfg = thrusterCfg(shape);
  // 噴口座標交給特效層掛火焰：[x, y, z, 口徑, 傾角]
  g.userData.nozzles =
      shape === 'thr-ring'  ? [[0, -0.02, -0.46, 0.52, 0.28]]
    : shape === 'thr-heavy' ? [[ 0.40, -0.30, -0.52, 0.34, 0.42], [-0.40, -0.30, -0.52, 0.34, 0.42]]
    :                         [[ 0.34, -0.32, -0.46, 0.26, 0.38], [-0.34, -0.32, -0.46, 0.26, 0.38]];
  g.userData.vents = shape === 'thr-heavy' ? [[0.62, 0.30, -0.12], [-0.62, 0.30, -0.12]]
                                           : [[0.44, 0.36, -0.16], [-0.44, 0.36, -0.16]];

  if (shape === 'thr-ring') {
    g.add(bevel(MAT_P.dark,  0.72, 0.40, 0.30, [0, 0.06, 0]));                     // 掛座
    g.add(mesh (G.tor,   MAT_P.armor, [1.06, 1.06, 0.30], [0, -0.02, -0.30], [0.28, 0, 0])); // 大推進環
    g.add(mesh (G.tor,   P.glow,  [0.80, 0.80, 0.22], [0, -0.02, -0.30], [0.28, 0, 0]));
    g.add(rib  (MAT_P.armor, 0.09, 0.09, 0.62, [ 0.50, 0.12, -0.20], 0.28, 0, -0.30));
    g.add(rib  (MAT_P.armor, 0.09, 0.09, 0.62, [-0.50, 0.12, -0.20], 0.28, 0,  0.30));
    g.add(mesh (G.box,   MAT_P.trim,  [0.14, 0.14, 0.34], [ 0.30, 0.14, -0.16]));
    g.add(mesh (G.box,   MAT_P.trim,  [0.14, 0.14, 0.34], [-0.30, 0.14, -0.16]));

  } else if (shape === 'thr-heavy') {
    g.add(bevel(MAT_P.dark,  0.98, 0.62, 0.44, [0, 0.10, -0.06]));                 // 大型背包
    g.add(plate(MAT_P.armor, 1.02, 0.20, 0.46, [0, 0.44, -0.08], -0.22));
    g.add(rib  (MAT_P.armor, 1.04, 0.08, 0.08, [0, 0.54, 0.10], -0.22));
    g.add(bevel(MAT_P.dark,  0.40, 0.40, 0.40, [ 0.40, -0.18, -0.30], [0.42, 0, 0])); // 噴口 ×2
    g.add(bevel(MAT_P.dark,  0.40, 0.40, 0.40, [-0.40, -0.18, -0.30], [0.42, 0, 0]));
    g.add(mesh (G.cone,  P.glow, [0.30, 0.30, 0.30], [ 0.40, -0.30, -0.42], [-Math.PI / 2 + 0.42, 0, 0]));
    g.add(mesh (G.cone,  P.glow, [0.30, 0.30, 0.30], [-0.40, -0.30, -0.42], [-Math.PI / 2 + 0.42, 0, 0]));
    g.add(mesh (G.box,   MAT_P.trim, [0.26, 0.44, 0.28], [ 0.62, 0.20, -0.12]));   // 外掛配重塊
    g.add(mesh (G.box,   MAT_P.trim, [0.26, 0.44, 0.28], [-0.62, 0.20, -0.12]));
    g.add(seam (0.94, 0.05, 0.40, [0, 0.30, -0.06]));

  } else {  /* thr-twin */
    g.add(bevel(MAT_P.dark,  0.80, 0.52, 0.36, [0, 0.08, -0.02]));                 // 背包本體
    g.add(plate(MAT_P.armor, 0.84, 0.18, 0.40, [0, 0.38, -0.04], -0.24));
    g.add(rib  (MAT_P.armor, 0.86, 0.08, 0.08, [0, 0.47, 0.13], -0.24));
    g.add(bevel(MAT_P.dark,  0.34, 0.46, 0.34, [ 0.34, -0.16, -0.24], [0.38, 0, 0])); // 方形噴口 ×2
    g.add(bevel(MAT_P.dark,  0.34, 0.46, 0.34, [-0.34, -0.16, -0.24], [0.38, 0, 0]));
    g.add(mesh (G.box,   P.glow, [0.22, 0.26, 0.06], [ 0.34, -0.32, -0.38], [0.38, 0, 0]));
    g.add(mesh (G.box,   P.glow, [0.22, 0.26, 0.06], [-0.34, -0.32, -0.38], [0.38, 0, 0]));
    g.add(plate(MAT_P.armor, 0.16, 0.34, 0.30, [ 0.50, 0.14, -0.06], 0, 0, -0.30)); // 側鰭（只一邊大）
    g.add(seam (0.76, 0.05, 0.34, [0, 0.26, -0.02]));
  }
  return g;
}
